/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

export interface DiscoveryMetadataFeature {
  id: string;
  conformsTo: string[];
  type: string;
  geometry: InfoDialogGeographicalCoverageProps;
  properties: {
    identifier?: string;
    title?: string;
    description?: string;
    formats?: {
      name: string;
    }[];
    themes?: InfoDialogThemesProps[];
    language?: {
      code: string;
    };
    type?: string;
    updated?: string;
    created?: string;
    rights?: string;
    contacts: InfoDialogContactsProps[];
    keywords?: string[];
    'wmo:dataPolicy': string;
  };
  links: InfoDialogLinksProps[];
  time?: InfoDialogTimeProps;
}

export interface InfoDialogBaseInfoProps {
  properties: {
    identifier?: string;
    title?: string;
    description?: string;
    formats?: {
      name: string;
    }[];
    themes?: InfoDialogThemesProps[];
    language?: {
      code: string;
    };
    type?: string;
    updated?: string;
    created?: string;
    rights?: string;
    contacts: InfoDialogContactsProps[];
    keywords?: string[];
    'wmo:dataPolicy': string;
  };
  datasetLink: string;
  id: string;
}

export interface InfoDialogGeographicalCoverageProps {
  type: string;
  coordinates: number[][][] | number[][][][];
}

export interface InfoDialogTimeProps {
  interval?: string[];
  resolution?: string;
}

export interface InfoDialogThemesAndKeywordsProps {
  themes?: InfoDialogThemesProps[];
  keywords?: string[];
}

export interface InfoDialogThemesProps {
  concepts: {
    id: string;
  }[];
  scheme: string;
}

export interface InfoDialogLinksProps {
  href: string;
  rel: string;
  title: string;
  type: string;
}

export interface InfoDialogContactsProps {
  addresses?: {
    administrativeArea?: string;
    city?: string;
    country?: string;
    deliveryPoint?: string[];
    postalCode?: string;
  }[];
  contactInstructions?: string;
  emails?: {
    value: string;
  }[];
  hoursOfService?: string;
  links?: {
    href: string;
    rel: string;
    type: string;
  }[];
  name?: string;
  organization?: string;
  phones?: {
    value: string;
  }[];
  position?: string;
  roles?: string[];
}

export interface DiscoveryMetadataItems {
  type: string;
  features: DiscoveryMetadataFeature[];
  numberMatched: number;
  numberReturned: number;
  links: object[];
  timeStamp: string;
}

export interface DataExplorerWMSState {
  geometry: {
    type: string;
    coordinates: number[][][] | number[][][][];
  };
  links: {
    href: string;
    rel: string;
    title?: string;
    type: string;
  }[];
}

export interface DataExplorerStoreType {
  discoveryCatalogueURL: string;
  filters: {
    searchFilter: string;
    queryables: Queryable[];
    concepts: Concept[];
    queryableConfigurations: QueryableConfiguration[];
    wmsbox?: number[];
    bbox?: number[];
    meteogateOnly?: boolean;
  };
  selectedDatasetId: string;
  areaFilterTileURL?: string;
  areaFilterTileLicense?: string;
  infoDialogWMSState?: DataExplorerWMSState;
}

export interface DataExplorerModuleState {
  dataExplorer?: DataExplorerStoreType;
}

export interface SetSearchFilterPayload {
  filterText: string;
}

export interface SetQueryablesPayload {
  queryables: Queryable[];
}

export interface SetQueryableValuePayload {
  id: string;
  value: string;
  checked: boolean;
}

export interface SetSelectedConceptPayload {
  id: string;
}

export interface SetSelectedDatasetPayload {
  id: string;
}

export interface SetBboxFilterPayload {
  bbox: number[];
}

export interface SetMeteogateOnlyPayload {
  enabled: boolean;
}

export interface Queryable {
  id: string;
  label: string;
  values: QueryableValue[];
  selected: string[];
}

export interface SetWMSboxFilterPayload {
  wmsBox: number[];
}

export interface QueryableValue {
  value: string;
  datasetCount: number;
}

export interface Concept {
  id: string;
  label: string;
  selected: boolean;
}

export enum DatasetFormat {
  WMS = 'WMS',
  GeoJSON = 'GeoJSON',
}

export interface AddLayerParams {
  serviceUrl: string;
  layerName: string;
  datasetFormat: DatasetFormat;
}

export interface DeleteLayerParams {
  layerId: string;
  layerIndex: number;
}

export interface ConceptConfiguration {
  id: string;
  label: string;
}

export interface QueryableConfiguration {
  id: string;
  label: string;
}

export interface DataExplorerPresets {
  discoveryCatalogueURL: string;
  concepts: ConceptConfiguration[];
  queryables: QueryableConfiguration[];
  areaFilterTileURL?: string;
  areaFilterTileLicense?: string;
}
